import {
  createAsyncThunk,
  createSlice,
} from "@reduxjs/toolkit";
import { SSO_STATE, STATUS } from "@/constants";
import {
  syncSsoLoginEnabledFromConfigurations,
} from "@/features/settings/model/ssoStateCache";
import { apiFetch } from "@/shared/api/apiFetch";
import { readApiErrorMessage } from "@/shared/api/parseApiErrorMessage";

type SsoConfigurationStateValue = (typeof SSO_STATE)[keyof typeof SSO_STATE];

export type SsoAttributeMappings = {
  email: string;
  given_name: string;
};

export type SsoConfiguration = {
  id: number;
  name: string;
  metadata_url: string;
  attribute_mappings: SsoAttributeMappings;
  state: SsoConfigurationStateValue;
  created_at: string;
  updated_at: string;
};

export type CreateSsoConfigurationPayload = {
  name: string;
  metadata_url: string;
  attribute_mappings: SsoAttributeMappings;
};

export type SsoConfigurationListState = {
  status: "idle" | "loading" | "succeeded" | "failed";
  error: string | null;
  configurations: SsoConfiguration[];
  /** Whether email/password sign-in has been turned off. `null` until the flag has loaded. */
  credentialLoginDisabled: boolean | null;
};

export const ssoConfigurationInitialState: SsoConfigurationListState = {
  status: STATUS.IDLE,
  error: null,
  configurations: [],
  credentialLoginDisabled: null,
};

function isSsoAttributeMappings(value: unknown): value is SsoAttributeMappings {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  const row = value as Record<string, unknown>;
  return typeof row.email === "string" && typeof row.given_name === "string";
}

function isSsoConfiguration(value: unknown): value is SsoConfiguration {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  const row = value as Record<string, unknown>;
  return (
    typeof row.id === "number" &&
    typeof row.name === "string" &&
    typeof row.metadata_url === "string" &&
    isSsoAttributeMappings(row.attribute_mappings) &&
    (row.state === SSO_STATE.CONFIGURED || row.state === SSO_STATE.ENABLED) &&
    typeof row.created_at === "string" &&
    typeof row.updated_at === "string"
  );
}

function isCredentialLoginResponse(
  value: unknown,
): value is { credential_login_disabled: boolean } {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  return typeof (value as Record<string, unknown>).credential_login_disabled === "boolean";
}

function parseSsoConfigurationsResponse(data: unknown): SsoConfiguration[] | null {
  if (!Array.isArray(data)) {
    return null;
  }
  if (!data.every(isSsoConfiguration)) {
    return null;
  }
  return data;
}

function replaceConfigurationInList(
  configurations: SsoConfiguration[],
  updated: SsoConfiguration,
): SsoConfiguration[] {
  const index = configurations.findIndex((config) => config.id === updated.id);
  if (index === -1) {
    return [...configurations, updated];
  }
  const next = [...configurations];
  next[index] = updated;
  return next;
}

export const fetchSsoConfigurations = createAsyncThunk<
  SsoConfiguration[],
  void,
  { rejectValue: string }
>("ssoConfiguration/fetch", async (_, { rejectWithValue }) => {
  try {
    const res = await apiFetch("/v1/sso-configurations/", { method: "GET" });
    if (!res.ok) {
      return rejectWithValue(await readApiErrorMessage(res));
    }
    const data: unknown = await res.json();
    const configurations = parseSsoConfigurationsResponse(data);
    if (!configurations) {
      return rejectWithValue("Unexpected response shape");
    }
    syncSsoLoginEnabledFromConfigurations(configurations);
    return configurations;
  } catch (error) {
    const message = error instanceof Error ? error.message : "Request failed";
    return rejectWithValue(message);
  }
});

export const createSsoConfiguration = createAsyncThunk<
  SsoConfiguration,
  CreateSsoConfigurationPayload,
  { rejectValue: string }
>("ssoConfiguration/create", async (payload, { rejectWithValue }) => {
  try {
    const res = await apiFetch("/v1/sso-configurations/", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      return rejectWithValue(await readApiErrorMessage(res));
    }
    const data: unknown = await res.json();
    if (!isSsoConfiguration(data)) {
      return rejectWithValue("Unexpected response shape");
    }
    return data;
  } catch (error) {
    const message = error instanceof Error ? error.message : "Request failed";
    return rejectWithValue(message);
  }
});

export const enableSsoConfiguration = createAsyncThunk<
  SsoConfiguration,
  number,
  { rejectValue: string }
>("ssoConfiguration/enable", async (configurationId, { rejectWithValue }) => {
  try {
    const res = await apiFetch(`/v1/sso-configurations/${configurationId}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ state: SSO_STATE.ENABLED }),
    });
    if (!res.ok) {
      return rejectWithValue(await readApiErrorMessage(res));
    }
    const data: unknown = await res.json();
    if (!isSsoConfiguration(data)) {
      return rejectWithValue("Unexpected response shape");
    }
    return data;
  } catch (error) {
    const message = error instanceof Error ? error.message : "Request failed";
    return rejectWithValue(message);
  }
});

export const fetchCredentialLogin = createAsyncThunk<
  boolean,
  void,
  { rejectValue: string }
>("ssoConfiguration/fetchCredentialLogin", async (_, { rejectWithValue }) => {
  try {
    const res = await apiFetch("/v1/sso-configurations/credential-login", { method: "GET" });
    if (!res.ok) {
      return rejectWithValue(await readApiErrorMessage(res));
    }
    const data: unknown = await res.json();
    if (!isCredentialLoginResponse(data)) {
      return rejectWithValue("Unexpected response shape");
    }
    return data.credential_login_disabled;
  } catch (error) {
    const message = error instanceof Error ? error.message : "Request failed";
    return rejectWithValue(message);
  }
});

/**
 * Turns email/password sign-in off for the tenant. One-way by design: Settings offers no way to
 * turn it back on, so this thunk only ever sends `disabled: true`.
 */
export const disableCredentialLogin = createAsyncThunk<
  boolean,
  void,
  { rejectValue: string }
>("ssoConfiguration/disableCredentialLogin", async (_, { rejectWithValue }) => {
  try {
    const res = await apiFetch("/v1/sso-configurations/credential-login", {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ disabled: true }),
    });
    if (!res.ok) {
      return rejectWithValue(await readApiErrorMessage(res));
    }
    const data: unknown = await res.json();
    if (!isCredentialLoginResponse(data)) {
      return rejectWithValue("Unexpected response shape");
    }
    return data.credential_login_disabled;
  } catch (error) {
    const message = error instanceof Error ? error.message : "Request failed";
    return rejectWithValue(message);
  }
});

const ssoConfigurationSlice = createSlice({
  name: "ssoConfiguration",
  initialState: ssoConfigurationInitialState,
  reducers: {},
  extraReducers(builder) {
    builder
      .addCase(fetchSsoConfigurations.pending, (state) => {
        state.status = STATUS.LOADING;
        state.error = null;
      })
      .addCase(fetchSsoConfigurations.fulfilled, (state, action) => {
        state.status = STATUS.SUCCEEDED;
        state.configurations = action.payload;
      })
      .addCase(fetchSsoConfigurations.rejected, (state, action) => {
        state.status = STATUS.FAILED;
        state.error = action.payload ?? "Request failed";
      })
      .addCase(createSsoConfiguration.fulfilled, (state, action) => {
        state.configurations = replaceConfigurationInList(state.configurations, action.payload);
        syncSsoLoginEnabledFromConfigurations(state.configurations);
        state.error = null;
      })
      .addCase(enableSsoConfiguration.fulfilled, (state, action) => {
        state.configurations = replaceConfigurationInList(state.configurations, action.payload);
        syncSsoLoginEnabledFromConfigurations(state.configurations);
        state.error = null;
      })
      .addCase(fetchCredentialLogin.fulfilled, (state, action) => {
        state.credentialLoginDisabled = action.payload;
      })
      .addCase(disableCredentialLogin.fulfilled, (state, action) => {
        state.credentialLoginDisabled = action.payload;
      });
  },
});

export const ssoConfigurationReducer = ssoConfigurationSlice.reducer;
